import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import SmartLink from '@/components/generals/smart-link'
import SoftlyLogo from '@/components/generals/softly-logo'
import { COPYRIGHT_YEAR, FOOTER_COLUMNS, siteConfig } from '@/constants/site'

const footerLinkClass = cn(
    'rounded-sm text-[15px] text-cocoa underline-offset-4 hover:underline',
    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cocoa'
)

const socialClass = cn(
    'grid size-11 place-items-center rounded-full border border-cocoa/25 text-cocoa transition-colors duration-500',
    'hover:border-cocoa hover:bg-cocoa hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
)

const SOCIALS = [
    {
        label: 'Softly on Facebook',
        href: siteConfig.links.facebook,
        icon: (
            <svg viewBox='0 0 24 24' className='size-4' fill='currentColor' aria-hidden='true' focusable='false'>
                <path d='M13.5 21v-7.6h2.6l.4-3.1h-3V8.4c0-.9.3-1.5 1.6-1.5h1.6V4.1c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.2H7.7v3.1h2.6V21h3.2Z' />
            </svg>
        )
    },
    {
        label: 'Softly on X',
        href: siteConfig.links.x,
        icon: (
            <svg viewBox='0 0 24 24' className='size-[15px]' fill='currentColor' aria-hidden='true' focusable='false'>
                <path d='M18.2 2.3h3.3l-7.2 8.3 8.5 11.2h-6.7l-5.2-6.8-6 6.8H1.7l7.7-8.8L1.3 2.3h6.8l4.7 6.2 5.4-6.2Zm-1.1 17.5h1.8L7.1 4.1h-2l12 15.7Z' />
            </svg>
        )
    },
    {
        label: 'Softly on Instagram',
        href: siteConfig.links.instagram,
        icon: (
            <svg viewBox='0 0 24 24' className='size-4' fill='none' stroke='currentColor' strokeWidth='1.6' aria-hidden='true' focusable='false'>
                <rect x='3.5' y='3.5' width='17' height='17' rx='5' />
                <circle cx='12' cy='12' r='4' />
                <circle cx='17.2' cy='6.8' r='0.9' fill='currentColor' stroke='none' />
            </svg>
        )
    }
]

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-apricot text-cocoa'>
            <div className='mx-auto max-w-[1440px] px-5 pt-14 lg:px-10 lg:pt-20'>
                <div className='h-px bg-cocoa/15' aria-hidden='true' />

                <div className='grid gap-12 pt-12 lg:grid-cols-12 lg:gap-8 lg:pt-16'>
                    <div id='about' className='scroll-mt-28 lg:col-span-4'>
                        <SmartLink
                            href='/'
                            aria-label='Softly, back to the top'
                            className='inline-block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cocoa'
                        >
                            <SoftlyLogo />
                        </SmartLink>

                        <p className='mt-5 max-w-[22rem] text-[15px] leading-relaxed text-cocoa/85'>
                            Softly is one gentle, fragrance-free balm for hands, knees, elbows and every dry spot in between. One tube, kept simple, made to be used everywhere.
                        </p>
                    </div>

                    <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-6'>
                        {FOOTER_COLUMNS.map(column => (
                            <div key={column.title}>
                                <h2 className='text-[12px] font-medium uppercase tracking-[0.18em] text-cocoa/85'>
                                    {column.title}
                                </h2>

                                <ul className='mt-5 space-y-3'>
                                    {column.links.map(link => (
                                        <li key={link.label}>
                                            <SmartLink href={link.href} className={footerLinkClass}>
                                                {link.label}
                                            </SmartLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className='flex gap-2.5 lg:col-span-2 lg:justify-end'>
                        {SOCIALS.map(social => (
                            <a
                                key={social.label}
                                href={social.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={social.label}
                                className={socialClass}
                            >
                                {social.icon}
                            </a>
                        ))}
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-2 border-t border-cocoa/15 pb-10 pt-6 text-[13px] text-cocoa/80 sm:flex-row sm:items-center sm:justify-between lg:mt-20 lg:pb-12'>
                    <p>
                        &copy; {COPYRIGHT_YEAR} {siteConfig.name}. All rights reserved.
                    </p>

                    <a
                        href={`mailto:${siteConfig.email}`}
                        className='self-start rounded-sm transition-colors duration-500 hover:text-cocoa focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa sm:self-auto'
                    >
                        {siteConfig.email}
                    </a>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
